import React, { useState } from "react"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Radio,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material"
import i18n from "@/i18n"
import { useQuery } from "@apollo/client/react"
import { GetVenuesDocument, Role } from "@/graphql/graphql"

interface UserRole {
  venueId: string
  role: Role
}

interface UserRolesEditorProps {
  value: UserRole[]
  onChange: (roles: UserRole[]) => void
}

const UserRolesEditor: React.FC<UserRolesEditorProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const [localRoles, setLocalRoles] = useState<UserRole[]>(value)

  const { data, loading } = useQuery(GetVenuesDocument)

  const venues = data?.venues || []

  const translateRole = (role: Role): string => {
    switch (role) {
      case Role.Admin:
        return i18n.t("app.settings.users.list.roles.admin")
      case Role.Manager:
        return i18n.t("app.settings.users.list.roles.manager")
      case Role.Staff:
        return i18n.t("app.settings.users.list.roles.staff")
      default:
        return ""
    }
  }

  const getUniqueRolesDisplay = (): string => {
    const uniqueRoles = new Set(value.map((r) => r.role))
    return Array.from(uniqueRoles)
      .map((role) => translateRole(role))
      .join(", ")
  }

  const handleOpen = () => {
    setLocalRoles(value)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleSave = () => {
    onChange(localRoles)
    setOpen(false)
  }

  const handleRoleChange = (venueId: string, role: Role | null) => {
    setLocalRoles((prev) => {
      // Remove existing role for this venue
      const filtered = prev.filter((r) => r.venueId !== venueId)

      // If role is not null, add new role
      if (role) {
        return [...filtered, { venueId, role }]
      }

      return filtered
    })
  }

  const getVenueRole = (venueId: string): Role | null => {
    const userRole = localRoles.find((r) => r.venueId === venueId)
    return userRole?.role || null
  }

  return (
    <>
      <TextField
        label={i18n.t("app.settings.users.list.role")}
        value={getUniqueRolesDisplay() || i18n.t("app.settings.users.list.no_roles")}
        onClick={handleOpen}
        fullWidth
        data-testid="settings-users-roles-editor-input"
        slotProps={{
          input: {
            readOnly: true,
            style: { cursor: "pointer" },
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpen}
                  data-testid="settings-users-roles-editor-btn-edit"
                >
                  {i18n.t("app.settings.users.list.edit_roles")}
                </Button>
              </InputAdornment>
            ),
          },
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        data-testid="settings-users-roles-editor-dialog"
      >
        <DialogTitle>{i18n.t("app.settings.users.list.roles_dialog_title")}</DialogTitle>
        <DialogContent>
          {loading ? (
            <div className="flex justify-center p-4">
              <CircularProgress />
            </div>
          ) : (
            <Table data-testid="settings-users-roles-editor-table">
              <TableHead>
                <TableRow>
                  <TableCell>Venue</TableCell>
                  <TableCell align="center">{i18n.t("app.settings.users.list.roles.admin")}</TableCell>
                  <TableCell align="center">{i18n.t("app.settings.users.list.roles.manager")}</TableCell>
                  <TableCell align="center">{i18n.t("app.settings.users.list.roles.staff")}</TableCell>
                  <TableCell align="center">None</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {venues.map((venue) => {
                  const currentRole = getVenueRole(venue.id)

                  return (
                    <TableRow key={venue.id} data-testid={`settings-users-roles-editor-row-${venue.id}`}>
                      <TableCell>{venue.name}</TableCell>
                      <TableCell align="center">
                        <Radio
                          checked={currentRole === Role.Admin}
                          onChange={() => handleRoleChange(venue.id, Role.Admin)}
                          data-testid={`settings-users-roles-editor-radio-admin-${venue.id}`}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Radio
                          checked={currentRole === Role.Manager}
                          onChange={() => handleRoleChange(venue.id, Role.Manager)}
                          data-testid={`settings-users-roles-editor-radio-manager-${venue.id}`}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Radio
                          checked={currentRole === Role.Staff}
                          onChange={() => handleRoleChange(venue.id, Role.Staff)}
                          data-testid={`settings-users-roles-editor-radio-staff-${venue.id}`}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Radio
                          checked={currentRole === null}
                          onChange={() => handleRoleChange(venue.id, null)}
                          data-testid={`settings-users-roles-editor-radio-none-${venue.id}`}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" data-testid="settings-users-roles-editor-btn-cancel">
            {i18n.t("app.actions.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            data-testid="settings-users-roles-editor-btn-save"
          >
            {i18n.t("app.actions.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserRolesEditor
